import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchRfpQuestionFindAnswers } from "../services/rfpsService";
import type { RfpQuestionFindAnswersResponse } from "../types/rfp";

type UseRfpQuestionFindAnswersArgs = {
  readonly rfpId: string | null;
  readonly questionId: string | null;
  readonly isOpen: boolean;
  readonly topN?: number;
};

export type UseRfpQuestionFindAnswersResult = {
  readonly findAnswers: RfpQuestionFindAnswersResponse | null;
  readonly loading: boolean;
  readonly error: string | null;
  readonly loadFindAnswers: () => Promise<void>;
};

export const useRfpQuestionFindAnswers = ({
  rfpId,
  questionId,
  isOpen,
  topN = 5,
}: UseRfpQuestionFindAnswersArgs): UseRfpQuestionFindAnswersResult => {
  const [findAnswers, setFindAnswers] = useState<RfpQuestionFindAnswersResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const latestRequestIdRef = useRef(0);

  const normalizedRfpId = rfpId?.trim() ?? "";
  const normalizedQuestionId = questionId?.trim() ?? "";
  const normalizedTopN = Number.isFinite(topN) ? Math.max(1, Math.floor(topN)) : 5;
  const contextKey = useMemo(
    () => `${normalizedRfpId}:${normalizedQuestionId}:${normalizedTopN}`,
    [normalizedQuestionId, normalizedRfpId, normalizedTopN],
  );
  const activeContextKeyRef = useRef(contextKey);

  useEffect(() => {
    activeContextKeyRef.current = contextKey;
  }, [contextKey]);

  useEffect(() => {
    if (!isOpen || !normalizedRfpId || !normalizedQuestionId) {
      setFindAnswers(null);
      setLoading(false);
      setError(null);
      return;
    }

    setFindAnswers(null);
    setError(null);
  }, [isOpen, normalizedQuestionId, normalizedRfpId, normalizedTopN]);

  const loadFindAnswers = useCallback(async () => {
    if (!normalizedRfpId || !normalizedQuestionId) {
      setFindAnswers(null);
      setLoading(false);
      setError(null);
      return;
    }

    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;
    const requestContextKey = `${normalizedRfpId}:${normalizedQuestionId}:${normalizedTopN}`;

    setLoading(true);
    setError(null);
    try {
      const response = await fetchRfpQuestionFindAnswers(
        normalizedRfpId,
        normalizedQuestionId,
        normalizedTopN,
      );
      if (
        latestRequestIdRef.current !== requestId ||
        activeContextKeyRef.current !== requestContextKey
      ) {
        return;
      }
      setFindAnswers(response);
    } catch (e) {
      if (
        latestRequestIdRef.current !== requestId ||
        activeContextKeyRef.current !== requestContextKey
      ) {
        return;
      }
      const message = e instanceof Error ? e.message : "Failed to load find answers";
      setError(message);
      setFindAnswers(null);
    } finally {
      if (
        latestRequestIdRef.current === requestId &&
        activeContextKeyRef.current === requestContextKey
      ) {
        setLoading(false);
      }
    }
  }, [normalizedQuestionId, normalizedRfpId, normalizedTopN]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    void loadFindAnswers();
  }, [isOpen, loadFindAnswers]);

  return {
    findAnswers,
    loading,
    error,
    loadFindAnswers,
  };
};
